<template>
    <div
        v-if="error"
        style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%)"
        class="has-text-centered"
    >
        {{ error }}
    </div>
</template>

<script>
import get from 'lodash/get.js';
import { useI18n } from 'vue-i18n';
import { createNamespacedHelpers } from 'vuex';

import { handleResponseError, initialState } from '@/Helpers.js';
import AutomatedSurveyAssignment from '@/models/AutomatedSurveyAssignment.js';
import MessageAlert from '@/models/MessageAlert.js';
import axios from '@/plugins/axios.js';
import SnackbarService from '@/services/SnackbarService.js';
import { canEnforceLocks, parseServerDate, trackUpcomingLock } from '@/utilities/automatedSurveyLockSchedule.js';
import { redirectIntoLockedAssignment, routeToAutomatedSurveyAssignment } from '@/utilities/automatedSurveyStart.js';
import { QUIZ_ATTEMPT_ROUTE_NAMES } from '@/utilities/sidebarMenuRoute.js';

export {
    createSurveyAttemptWithRetry,
    redirectIntoLockedAssignment,
    routeToAutomatedSurveyAssignment,
    startDueLockedAutomatedSurvey,
} from '@/utilities/automatedSurveyStart.js';

const { mapState } = createNamespacedHelpers('Auth');

// Staff are exempt unless they are a Student where it was triggered (server-computed).
const isEnforcedLockNow = assignment => assignment.is_locked_now && assignment.is_lock_enforced !== false;

// Required (not locked) must be completed but never locks anyone out: its prompt has no "Later".
const isRequiredNow = assignment =>
    !!assignment.required_at && parseServerDate(assignment.required_at) <= Date.now() && !assignment.completed_at;

// noinspection LocalVariableNamingConventionJS
export const AutomatedSurveyStarterMixin = {
    methods: {
        async startSurvey(surveyAssignment) {
            const loader = this.$loading.open({});

            try {
                const route = await routeToAutomatedSurveyAssignment(surveyAssignment);

                if (route.name) {
                    window.location.href = this.$router.resolve(route).href;
                }

                loader.close();
            } catch (e) {
                handleResponseError(e);

                loader.close();
            }
        },
    },
};

export default {
    name: 'AutomatedSurveyChecker',

    mixins: [AutomatedSurveyStarterMixin],

    emits: ['complete'],

    data: () => ({
        error: '',
        timeoutChecker: null,
    }),

    computed: {
        ...mapState(['user']),
    },

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    watch: {
        // Watch for route changes to re-check surveys when returning from completion
        $route(to, from) {
            // If coming back from an automated survey attempt, re-check for more locked surveys
            if (from?.name === 'automated-survey-attempt' && to?.name !== 'automated-survey-attempt') {
                this.performSurveyCheck();
            }
        },
    },

    created() {
        // Check initial state availability
        const initialAssignments = initialState('AutomatedSurveyAssignments', []);

        this.listenForLockedAutomatedSurveys();
        this.performSurveyCheck();
    },

    mounted() {
        // Double-check if we should run the check again after mounting
        if (this.$route.name !== 'automated-survey-attempt' && !this.$store.getters['User/hasActiveQuiz']) {
            // Optionally re-run the check if conditions haven't changed
        }
    },

    beforeUnmount() {
        this.error = '';

        if (this.user) {
            this.$echo.leave(`users-${this.user.id}`);
        }
    },

    methods: {
        performSurveyCheck() {
            if (this.$route.name === 'automated-survey-attempt') {
                this.$emit('complete');
                return;
            }

            // Check if user has an active quiz
            const userHasQuizActiveId = this.$store.getters['User/hasActiveQuiz'];

            // Proceed with navigation if user has an active quiz
            if (userHasQuizActiveId) {
                this.$emit('complete');
                return;
            }

            this.startLockedAutomatedSurveys();
        },

        listenForLockedAutomatedSurveys() {
            if (!this.user) {
                return;
            }

            this.$echo.private(`users-${this.user.id}`).notification(async notification => {
                if (notification.type !== 'App\\Notifications\\AutomatedSurveyTriggeredNotification') {
                    return;
                }

                await AutomatedSurveyAssignment.insert({ data: notification.assignment });

                const assignment = AutomatedSurveyAssignment.find(notification.assignment.id);

                if (isEnforcedLockNow(assignment)) {
                    this.$store.commit('SET_AUTOMATED_SURVEY_ASSIGNMENT_READY', assignment);
                    this.$store.commit('SET_OUT_OF_DATE');
                } else {
                    // Locks later: the next navigation after it comes due enforces it.
                    trackUpcomingLock(assignment);
                }

                if (
                    this.user.quiz_active_id ||
                    [...QUIZ_ATTEMPT_ROUTE_NAMES, 'automated-survey-attempt'].includes(this.$route.name)
                ) {
                    return;
                }

                // Broadcasts deliberately omit relations to stay within the payload limit.
                // Use the same hydrated, recipient-filtered alerts as a dashboard reload.
                try {
                    await MessageAlert.fetch();
                } catch (error) {
                    handleResponseError(error);
                }

                this.openSurveyPrompt(assignment);
            });
        },

        openSurveyPrompt(assignment) {
            if (QUIZ_ATTEMPT_ROUTE_NAMES.includes(this.$route.name)) {
                return;
            }

            const isCourseEvaluation = Boolean(assignment.course_id);

            let message = this.translate(
                isCourseEvaluation ? 'you_have_a_new_automated_survey' : 'you_have_a_new_standalone_automated_survey'
            );

            if (
                assignment.is_anonymous &&
                initialState('InstitutionSettings.automated_surveys.anonymous_notification_banner', true)
            ) {
                message += ` ${this.translate(
                    isCourseEvaluation
                        ? 'this_automated_survey_is_anonymous'
                        : 'this_standalone_automated_survey_is_anonymous'
                )}`;
            }

            const params = {
                type: 'is-primary',
                position: 'is-bottom-left',
                message,
                duration: 5000,
                queue: true,
                indefinite: true,
                actionText: isCourseEvaluation ? 'Start Evaluation' : 'Start Survey',
                onAction: () => this.startSurvey(assignment),
            };

            // "Later" only for surveys that are neither required nor (enforced) locked yet.
            if (!isEnforcedLockNow(assignment) && !isRequiredNow(assignment)) {
                params.cancelText = 'Later';
            }

            const snackbarNotification = this.$snackbar.open(params);

            this.$store.commit('AutomatedSurveys/SET_NOTIFICATION', snackbarNotification);
        },

        /**
         * Required surveys re-prompt on every page load until completed; nothing about
         * the prompt is persisted, so closing it only lasts until the next load.
         */
        promptForRequiredSurvey() {
            if (['quiz-attempt', 'automated-survey-attempt', 'survey-attempt'].includes(this.$route.name)) {
                return;
            }

            const required = AutomatedSurveyAssignment.query()
                .where('user_id', this.user?.id)
                .where('completed_at', null)
                .get()
                .find(assignment => isRequiredNow(assignment) && !isEnforcedLockNow(assignment));

            if (required) {
                this.openSurveyPrompt(required);
            }
        },

        async startLockedAutomatedSurveys() {
            this.timeoutChecker = setTimeout(() => {
                this.error =
                    'An error occurred while checking for surveys to complete. Please try refreshing the page or contacting support.';
            }, 1000 * 20);

            try {
                // Load assignments from initial state if any
                const loadedAssignments = initialState('AutomatedSurveyAssignments', []);

                if (loadedAssignments.length > 0) {
                    await AutomatedSurveyAssignment.insert({ data: loadedAssignments });
                }

                // The start endpoint would reject this user (deployment targets can be
                // any role): never auto-start, or every page load would fail.
                if (!canEnforceLocks()) {
                    this.promptForRequiredSurvey();

                    return true;
                }

                // Find locked assignments for the user that are not completed
                // Note: LoadingState already provides these in proper queue order (backend queue ordering)
                // is_locked_now only checks locked_at (for auto-start behavior)
                // Assignments with past required_at but future locked_at should NOT auto-start
                const lockedAssignments = AutomatedSurveyAssignment.query()
                    .where('user_id', this.user?.id)
                    .where(isEnforcedLockNow)
                    .where('completed_at', null)
                    .get();

                if (lockedAssignments.length === 0) {
                    this.promptForRequiredSurvey();
                }

                // Redirect if there are any locked assignments
                if (lockedAssignments.length > 0) {
                    // Take the first assignment - backend queue ordering ensures this is the correct next survey
                    const nextAssignment = lockedAssignments[0];

                    // Redirect to 'automated-survey-attempt' if conditions are met
                    if (nextAssignment.survey_attempt_id && this.$route.name !== 'automated-survey-attempt') {
                        this.complete();

                        return this.$router.push({
                            name: 'automated-survey-attempt',
                            params: {
                                id: nextAssignment.survey_id,
                                assignmentId: nextAssignment.id,
                                attemptId: nextAssignment.survey_attempt_id,
                            },
                        });
                    }

                    // Calculate route for the next assignment in queue with retry logic
                    try {
                        await redirectIntoLockedAssignment(nextAssignment, this.$router);

                        return true;
                    } catch (e) {
                        console.error('[DEBUG-AutomatedSurveyChecker] Failed to start survey after retries:', e);
                        this.error = `Unable to start survey: ${e.message}. Please refresh the page or contact support.`;
                        handleResponseError(e);
                    }
                }
            } catch (e) {
                this.error = e.message;
            } finally {
                this.complete();
            }

            return true;
        },

        complete() {
            this.$emit('complete');

            clearTimeout(this.timeoutChecker);
        },

        async checkForPrefilledAnswers(assignment) {
            try {
                const { data } = await axios.post(
                    '/api/automated-survey-attempts',
                    { automated_survey_assignment_id: assignment.id },
                    { errorHandle: false }
                );

                // Check if there are pre-filled answers
                if (data.data.prefilled_questions_count > 0) {
                    const totalQuestions = data.data.total_questions_count;
                    const prefilledCount = data.data.prefilled_questions_count;

                    await this.showPrefilledModal(assignment, prefilledCount, totalQuestions);
                }

                return data;
            } catch (e) {
                // Handle existing attempt redirect logic
                const otherAttempt = get(e, 'response.data.meta.other_attempt');
                if (otherAttempt) {
                    SnackbarService.open({
                        message: 'You already have an active survey attempt. Redirecting...',
                        type: 'is-danger',
                        duration: 5000,
                    });
                }
                throw e;
            }
        },

        async showPrefilledModal(assignment, prefilledCount, totalQuestions) {
            const isEvaluation = !!assignment.course_id;
            const itemType = isEvaluation ? 'evaluation' : 'survey';
            const remainingCount = totalQuestions - prefilledCount;

            let message = `This ${itemType} contains questions you've answered before. `;
            message += `${prefilledCount} out of ${totalQuestions} questions have been pre-filled with your previous answers to save you time.`;

            if (remainingCount > 0) {
                message += ` You have ${remainingCount} new question${remainingCount === 1 ? '' : 's'} to complete.`;
            }

            return new Promise(resolve => {
                this.$modal.open({
                    parent: this,
                    component: {
                        template: `
                            <div class="modal-card" style="width: auto">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">
                                        <e-icon icon="info-circle" type="is-info"></e-icon>
                                        Pre-filled ${isEvaluation ? 'Evaluation' : 'Survey'}
                                    </p>
                                </header>
                                <section class="modal-card-body">
                                    <div class="content">
                                        <p>${message}</p>
                                        <p class="has-text-subtle is-size-7">
                                            You can review and modify any pre-filled answers during the ${itemType}.
                                        </p>
                                    </div>
                                </section>
                                <footer class="modal-card-foot">
                                    <e-button @click="$emit('close')" type="is-primary">
                                        Continue
                                    </e-button>
                                    <e-button @click="$emit('close')" type="is-light">
                                        View Details Later
                                    </e-button>
                                </footer>
                            </div>
                        `,
                        props: ['isEvaluation'],
                        data: () => ({ isEvaluation, message }),
                    },
                    hasModalCard: true,
                    onCancel: resolve,
                    customClass: 'prefilled-survey-modal',
                });
            });
        },
    },
};
</script>
